// This is for Droppable containers that are used in the DragNDropTasks component
// So things like the TaskDroppableContainer and the TaskDroppableCard are used here

import { useDroppable } from "@dnd-kit/core"
import { useState } from "react"
import { EmployeeNameEmail } from "../../../../interfaces/employee_type"
import { SelectionComponent } from "../../../Buttons"
import { DroppableContainerProps } from "../../../../interfaces/draggable_types"
import { DraggableCard } from "./Draggable"

type DroppableProps = {
    id: string
    children: React.ReactNode
    title: string
    disabled?: boolean
    onRemove: () => void
    employee_options: EmployeeNameEmail[]
    onEmployeeSelect: (oldId: string, newEmployee: EmployeeNameEmail) => void
}

/**
 * This is a component that is used to show a droppable container
 * 
 * @param id - The id of the droppable container
 * @param title - The title of the droppable container
 * @param children - The children of the droppable container
 * @param onRemove - The function to remove an employee from a droppable container
 * @param disabled - Whether the droppable container is disabled
 * @param employee_options - The employees that are available to be assigned to the tasks
 * @param onEmployeeSelect - The function to select an employee for a task
 * @returns 
 */
export function TaskDroppableContainer({id, title, children, onRemove, disabled=false, employee_options, onEmployeeSelect}: DroppableProps){
    const [isRemoveHovered, setIsRemoveHovered] = useState<boolean>(false) // This is used to change the color of the droppable container when the remove button is hovered
    const { setNodeRef, isOver } = useDroppable({
        id: id,
        disabled: disabled
    })
    
    const onSelectEmployee = (event: unknown) => {
        if (event && typeof event === 'object' && 'label' in event && typeof event.label === 'string') {
            const employee = event as EmployeeNameEmail
            onEmployeeSelect(id, employee)
        }
    }

    const bgColor = isRemoveHovered ? 'bg-red-50 dark:bg-red-900' : isOver ? 'bg-green-50 dark:bg-green-900' : 'bg-stone-50 dark:bg-stone-800'

    return (
        <div className='flex flex-col h-[1100px]'>
            <div ref={setNodeRef} 
                className={`flex flex-col border border-stone-300 dark:border-stone-700 rounded-md p-2 m-2 min-h-[200px] min-w-64  transition-all duration-300 items-center  ${bgColor}`}
            >
                <SelectionComponent 
                    options={employee_options} 
                    name='employee' 
                    Value={title || 'Select an Employee'} 
                    onChange={onSelectEmployee} 
                />

                <div className='flex flex-col overflow-y-auto overflow-x-hidden m-2'>
                    {children}
                </div>
            </div>

            <button 
                className='bg-red-500 text-white p-2 m-2 rounded-md hover:bg-red-600 transition-all duration-300' 
                onMouseEnter={() => setIsRemoveHovered(true)}
                onMouseLeave={() => setIsRemoveHovered(false)}
                onClick={onRemove}
            >
                Remove Employee
            </button>
        </div>
    )
}

/**
 * This is a component that is used to show a dynamic number of droppable containers
 * 
 * @param droppables - The names of the droppable containers (this is the email of the employee)
 * @param tasksCards - The tasks that are being assigned to the employees
 * @param employee_options - The employees that are available to be assigned to the tasks
 * @param onRemove - The function to remove an employee from a droppable container
 * @param onEmployeeSelect - The function to select an employee for a task
 */
export function DynamicTaskDroppableContainer({droppables, tasksCards, employee_options, onRemove, onEmployeeSelect}: DroppableContainerProps){
    return (
        <div className='flex flex-row w-full h-full z-10 overflow-x-auto'>
            {droppables.map((id, index) => (
                <TaskDroppableContainer 
                    id={id} 
                    key={index} 
                    title={id} 
                    onRemove={() => onRemove(id)} 
                    employee_options={employee_options} 
                    onEmployeeSelect={onEmployeeSelect}
                >
                    {tasksCards.filter((card) => card.parent === id).map((card) => (
                        <DraggableCard 
                            key={card.id} 
                            id={card.id} 
                            title={card.title} 
                            description={card.description} 
                            category={card.category}
                            parent={card.parent} 
                            selected_employee={card.selected_employee}
                        />
                    ))}
                </TaskDroppableContainer>
            ))}
        </div>
    )
}